import React, { useState } from 'react';
import { Capacitor, registerPlugin } from '@capacitor/core';
import authService from '../services/authService';
import { onboardingHref } from '../utils/returnTo';
import './portalkit/kit.css';

/**
 * Android's system account picker (GoogleAuthPlugin.java). Not present in a build
 * made before Sept 25 2026, which is why every call site checks first.
 */
const GoogleAuth = registerPlugin('GoogleAuth');

const GOOGLE_AUTH_ENDPOINT = 'https://accounts.google.com/o/oauth2/v2/auth';
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

// The path Google redirects back to after consent. Must be registered (combined
// with the current origin) as an Authorized redirect URI in Google Cloud Console:
//   http://localhost:3000/auth/google/callback
//   https://party-resume.retic.co/auth/google/callback
export const GOOGLE_CALLBACK_PATH = '/auth/google/callback';

/**
 * Does this document load exist only to finish an OAuth round-trip?
 *
 * Signing in with Google boots the whole SPA TWICE: once on the callback path, purely to spend
 * the authorization code, and again on the real destination (`landAfterGoogleSignIn` hard-
 * navigates so every context reloads against the new session). `PortalRouter` opens its entry
 * ring on each boot, so one sign-in played the portal, showed "Signing you in…", and then played
 * the portal again — two entrances for one arrival.
 *
 * The callback is machinery, and machinery does not get an entrance. Only the second boot is a
 * place anybody asked to be, so it keeps the ring and it is the one that reveals where you were
 * going.
 */
export function isOauthHandoffPath(pathname) {
  return pathname === GOOGLE_CALLBACK_PATH;
}

// Google's iOS OAuth client. A native app is a PUBLIC client: it ships no
// secret, so the web client can't be reused (and wouldn't accept a custom-scheme
// redirect anyway). Proof of possession comes from PKCE instead.
const IOS_CLIENT_ID = process.env.REACT_APP_GOOGLE_IOS_CLIENT_ID
  || '201964672158-9tb86hemv8nvljlvofuoe2ooravsd0n0.apps.googleusercontent.com';
// Android has no client id here on purpose. Google disabled custom URI schemes for
// Android OAuth clients, so the reversed-client-id redirect below is answered with
// "Error 400: invalid_request — Custom URI scheme is not enabled for your Android
// client" before consent is even drawn. Android goes through the OS account picker
// instead (see nativeGoogleSignIn) and authorizes against the WEB client id; the two
// Android clients still exist in the Cloud project, because they are what let this
// package + signing certificate ask for a token at all, but nothing sends them.

/** Google requires the redirect to be the client id reversed, one slash. */
const reversedRedirect = (clientId) => `com.googleusercontent.apps.${clientId.split('.')[0]}:/oauth2redirect`;
export const PKCE_VERIFIER_KEY = 'googleOAuthVerifier';

/**
 * Where Google sends the user back.
 *
 * On the web that's this origin. In the native app it CANNOT be: Capacitor
 * serves the bundle from `partyresume://localhost`, and a Web OAuth client (the
 * kind our backend exchanges with, using a client secret) only accepts http/https
 * redirects. Google answers a custom scheme with
 * "Error 400: invalid_request — doesn't comply with Google's OAuth 2.0 policy".
 *
 * So iOS comes back on the reversed client id instead, which CapacitorBootstrap's
 * appUrlOpen turns into the /auth/google/callback route. (Going out to the real
 * https callback and relying on the Universal Link to bounce back was tested and
 * does not work — iOS skips Universal Links on cross-domain OAuth redirects and the
 * user ends up signed in on the website with the app still logged out.)
 *
 * Both the authorize request and the token exchange call this, so the two always
 * agree — Google rejects the exchange if the redirect_uri differs by a character.
 */
export function getGoogleRedirectUri() {
  if (Capacitor.isNativePlatform()) return reversedRedirect(getGoogleClientId());
  return `${window.location.origin}${GOOGLE_CALLBACK_PATH}`;
}

/** Which OAuth client this platform authorizes against. Android never gets here. */
export function getGoogleClientId() {
  return Capacitor.isNativePlatform() ? IOS_CLIENT_ID : GOOGLE_CLIENT_ID;
}

/**
 * What to put on screen when the Android account picker refuses.
 *
 * This used to be `e.message`, and Credential Manager routinely hands back an EMPTY message —
 * everything it knows is in the exception's type, which the plugin now sends as `reason`. An
 * empty string is still a string, so the old code set the error state to "" and `{error && …}`
 * rendered nothing at all: the picker closed and the screen looked exactly as it had before.
 * That is what "it comes back to the app and doesn't do anything" was.
 *
 * Nothing here may return a blank string. A failure the person cannot see is a failure nobody
 * can report, and the reason is the one clue that separates a transient refusal from this app's
 * package + signing certificate not being registered on an Android OAuth client.
 */
export function pickerFailureMessage(e) {
  const parts = [e?.data?.detail, e?.message, e?.data?.reason]
    .map((part) => (typeof part === 'string' ? part.trim() : ''))
    .filter((part) => part.length > 0);

  if (e?.code === 'GOOGLE_NO_ACCOUNT') {
    const why = parts.find((part) => !/no .*account/i.test(part));
    return why ? `No Google account on this device \u2014 ${why}` : 'No Google account on this device.';
  }

  // A cancellation is the one outcome that is usually innocent, so it stays quiet — but it is
  // ALSO how Play services reports a refusal it could not complete, and that one arrives with a
  // real complaint attached ("[28444] Developer console is not set up correctly."). Anything
  // that merely says "cancelled" is the boilerplate of a genuine back-press and is dropped;
  // anything else is Google telling us why, and goes on screen.
  if (e?.code === 'GOOGLE_CANCELLED') {
    const why = parts.find((part) => !/cancel/i.test(part));
    return why ? `Sign-in didn\u2019t finish \u2014 ${why}` : 'Sign-in didn\u2019t finish.';
  }

  return parts.length ? `Google sign-in failed \u2014 ${parts[0]}` : 'Google sign-in failed.';
}

/**
 * Android signs in through the OS account picker and never opens a browser, so none
 * of the redirect machinery below applies to it: no state, no PKCE, no callback page.
 */
export function googleUsesNativePicker() {
  return Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android';
}

/** base64url — PKCE forbids +, / and = padding. */
function base64Url(bytes) {
  let bin = '';
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/**
 * A PKCE verifier and its S256 challenge.
 *
 * The verifier stays on the device and is replayed at token exchange; only the
 * hash travels through the browser. That's what makes a secretless public client
 * safe — an attacker who intercepts the authorization code can't spend it.
 */
export async function createPkcePair() {
  const bytes = new Uint8Array(32);
  window.crypto.getRandomValues(bytes);
  const verifier = base64Url(bytes);
  const digest = await window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  return { verifier, challenge: base64Url(new Uint8Array(digest)) };
}

function randomState() {
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Where a completed Google sign-in lands.
 *
 * Shared by the two ways one can finish: the callback page after a browser round-trip,
 * and the Android picker, which never leaves the app. A hard navigation rather than a
 * route change, so every context reloads against the new session (mirrors LoginPage).
 */
export function landAfterGoogleSignIn(result, redirect) {
  // Sign-up v3: an account created by THIS sign-in walks the people-first flow (`/onboard`),
  // carrying where it was headed as `next`. A returning account — even one that never finished
  // the flow — goes straight on; the flow is never forced on a return visit.
  const isNew = !!result.data?.isNewAccount || !result.data?.user?.profile?.displayName;
  if (!isNew) {
    window.location.href = redirect || '/square';
    return;
  }
  // Pre-fill onboarding's name input with the Google account name (still editable).
  const suggestedName = result.data?.suggestedDisplayName;
  if (suggestedName) localStorage.setItem('googleSuggestedName', suggestedName);
  window.location.href = onboardingHref(redirect);
}


/**
 * GoogleSignInButton — starts a Google sign-in, by whichever route the platform has.
 *
 * Web and iOS take the OAuth 2.0 Authorization Code flow: a CSRF `state` and the
 * post-login destination go into sessionStorage and the browser leaves for Google's
 * consent screen. Android can't — Google disabled custom URI schemes for Android
 * clients — so it asks the OS for an ID token in place and finishes here.
 *
 * @param {string} [redirect] - where to land after a successful sign-in
 * @param {string} [label] - button text (defaults to "Continue with Google")
 */
/**
 * `variant="pill"` is the auth-screen look (design_handoff_auth_profile_planner, Sept 27 2026):
 * a 52px outline pill on black with the 18px four-colour G. The default stays the bordered slab.
 */
export default function GoogleSignInButton({ redirect = '/square', label = 'Continue with Google', disabled, onBeforeRedirect, variant = 'slab' }) {
  const notConfigured = !GOOGLE_CLIENT_ID;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Android: the system account picker hands back an ID token and the backend
   * verifies it. Nothing is stored between steps because there is no round-trip.
   */
  const signInWithPicker = async () => {
    if (!Capacitor.isPluginAvailable('GoogleAuth')) {
      // An app installed before the plugin shipped. A web fallback is no use — it
      // would sign them in on the website and leave the app logged out.
      setError('Update the app to sign in with Google.');
      return;
    }

    setBusy(true);
    try {
      // The audience is the WEB client id: it identifies this backend, which is what
      // verifies the token. The Android clients authorize the app, never the token.
      const { idToken } = await GoogleAuth.signIn({ serverClientId: GOOGLE_CLIENT_ID });
      if (!idToken) {
        setError('Google sign-in did not complete.');
        setBusy(false);
        return;
      }

      const result = await authService.googleIdTokenLogin(idToken);
      if (!result.success) {
        setError(result.error || 'Google sign-in failed.');
        setBusy(false);
        return;
      }
      landAfterGoogleSignIn(result, redirect);
    } catch (e) {
      // Every outcome says something now, a cancel included. Credential Manager cannot tell a
      // back-press apart from a refusal it decided to report as one, and an attempt that ends in
      // silence is unreportable: the picker closes, the screen looks untouched, and all anyone
      // can say is "it doesn't do anything". One quiet line costs a person who really did back
      // out nothing, and is the difference between a bug report and a shrug.
      setError(pickerFailureMessage(e));
      setBusy(false);
    }
  };

  const handleClick = async () => {
    if (notConfigured) return;
    setError(null);

    // Add-account mode uses this to snapshot the current account before we leave,
    // so it's still saved to switch back to after the new one signs in.
    onBeforeRedirect?.();

    if (googleUsesNativePicker()) return signInWithPicker();

    const state = randomState();
    sessionStorage.setItem('googleOAuthState', state);
    sessionStorage.setItem('googleOAuthRedirect', redirect || '/square');

    const native = Capacitor.isNativePlatform();
    const params = new URLSearchParams({
      client_id: getGoogleClientId(),
      redirect_uri: getGoogleRedirectUri(),
      response_type: 'code',
      scope: 'openid email profile',
      state,
      access_type: 'online',
      prompt: 'select_account',
    });

    if (native) {
      const { verifier, challenge } = await createPkcePair();
      sessionStorage.setItem(PKCE_VERIFIER_KEY, verifier);
      params.set('code_challenge', challenge);
      params.set('code_challenge_method', 'S256');
    }

    // accounts.google.com isn't in the shell's allowNavigation list, so Capacitor
    // hands this to the system browser — which is also what Google requires: it
    // refuses to render consent inside an embedded WebView (disallowed_useragent).
    window.location.href = `${GOOGLE_AUTH_ENDPOINT}?${params.toString()}`;
  };

  return (
    <div className="w-full">
      <button
        type="button"
        onClick={handleClick}
        disabled={disabled || notConfigured || busy}
        title={notConfigured ? 'Google sign-in is not configured' : undefined}
        className={variant === 'pill'
          ? 'pk pk-btn pk-outline-on-black w-full'
          : variant === 'enter'
            // Sign-up v3's ENTER card: the 56px white primary pill.
            ? 'pk pk-btn pk-primary-on-black w-full'
          : variant === 'whitePill'
            ? 'th-google w-full font-mono'
            : 'w-full flex items-center justify-center gap-3 border-2 border-black bg-white px-4 py-3 font-mono text-sm uppercase tracking-wider text-black transition-colors hover:bg-black hover:text-white disabled:opacity-40 disabled:cursor-not-allowed'}
        style={variant === 'pill'
          ? { height: 52, gap: 12, fontSize: 12, fontWeight: 700, letterSpacing: '.14em', textTransform: 'uppercase' }
          : variant === 'enter'
            ? { height: 56, gap: 12, fontSize: 12, fontWeight: 800, letterSpacing: '.16em', textTransform: 'uppercase' }
          : variant === 'whitePill'
            // The Portal panel's version: a white pill on black (the add-account sheet).
            ? { height: 48, gap: 12, fontSize: 12, fontWeight: 800, letterSpacing: '.14em', textTransform: 'uppercase' }
            : undefined}
      >
        <svg className={variant === 'pill' || variant === 'whitePill' || variant === 'enter' ? 'shrink-0' : 'h-5 w-5 shrink-0'} width={variant === 'pill' || variant === 'whitePill' || variant === 'enter' ? 18 : undefined} height={variant === 'pill' || variant === 'whitePill' || variant === 'enter' ? 18 : undefined} viewBox="0 0 24 24" aria-hidden="true">
          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z" />
          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z" />
          <path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z" />
          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z" />
        </svg>
        {busy ? 'Signing in…' : label}
      </button>
      {error && <p className={variant === 'pill' || variant === 'whitePill' ? 'mt-2 font-mono text-[10px] font-semibold uppercase tracking-wider' : 'mt-2 font-mono text-xs text-red-600'} style={variant === 'pill' || variant === 'whitePill' ? { color: '#FF66C4' } : undefined}>{error}</p>}
    </div>
  );
}
